import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ElementAttribute } from '@models/editor';
import { CanvasService } from '@service/canvas/canvas.service';
import { LibraryComponentSocketService } from '@service/library-component-socket.service';
import { LibraryComponentService } from '@service/library-component.service';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { LibraryComponent } from 'icreate/common/models/library-component';
import { firstValueFrom, fromEvent, Subject, takeUntil } from 'rxjs';
import { DuplicateMimicDialogComponent } from '../../duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';

@Component({
    selector: 'app-library-component-dialog',
    templateUrl: './library-component-dialog.component.html',
    styleUrls: ['./library-component-dialog.component.css'],
    standalone: false,
})
export class LibraryComponentDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly MAX_HEIGHT = 130;

    @ViewChildren('svgHost') svgHosts!: QueryList<ElementRef<SVGSVGElement>>;
    @ViewChild('componentsContainer') componentsContainer: ElementRef;

    @Input() selectedCategory!: LibraryCategory;
    @Input() x: number = 0;
    @Input() y: number = 0;
    @Input() componentList: LibraryComponent[] = [];
    @Input() whiteMode: boolean = false;
    @Input() isSettingCondition: boolean = false;
    @Input() selectedComponent: LibraryComponent = undefined;

    @Output() onSelectComponent = new EventEmitter<LibraryComponent>();
    @Output() onDeleteComponent = new EventEmitter<LibraryComponent>();

    canvas: any;
    showComponentOption: boolean = false;

    componentSvg: any;
    optionsTop: string;
    optionsLeft: string;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private libraryComponentService: LibraryComponentService,
        private canvasService: CanvasService,
        private dialog: MatDialog,
        private libraryComponentSocketService: LibraryComponentSocketService,
        private cdr: ChangeDetectorRef
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.showComponentOption) {
            this.showComponentOption = false;
        }
        if (this.selectedComponent && !this.isSettingCondition) {
            this.selectedComponent = undefined;
        }
    }

    @HostListener('document:contextmenu', ['$event'])
    closeContext(event: MouseEvent) {
        if (this.showComponentOption) {
            this.showComponentOption = false;
        }
        if (this.selectedComponent) {
            this.selectedComponent = undefined;
        }
    }

    ngOnInit(): void {
        this.canvas = this.canvasService.getCanvas();

        this.libraryComponentSocketService
            .libraryComponentCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newLibraryComponent: LibraryComponent) => {
                if (this.selectedCategory?.id === newLibraryComponent.libraryCategoryId) {
                    this.componentList.push(newLibraryComponent);
                }
            });

        this.libraryComponentSocketService
            .libraryComponentUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedLibraryComponent: LibraryComponent) => {
                if (this.selectedCategory?.id === updatedLibraryComponent.libraryCategoryId) {
                    this.componentList.forEach((component, index) => {
                        if (component.id === updatedLibraryComponent.id) {
                            this.componentList[index] = updatedLibraryComponent;
                            return;
                        }
                    });
                }
            });

        this.libraryComponentSocketService
            .libraryComponentDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((libraryComponentId: string) => {
                let indexToDelete = this.componentList.findIndex(library => library.id === libraryComponentId);
                if (indexToDelete !== -1) {
                    this.componentList.splice(indexToDelete, 1);
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.componentsContainer) {
            fromEvent(this.componentsContainer.nativeElement, 'scroll')
                .pipe(takeUntil(this.destroySubscriptionSubject$))
                .subscribe(() => {
                    this.showComponentOption = false;
                });
        }
    }

    ngAfterViewChecked(): void {
        this.svgHosts.forEach((ref, i) => this.appendFragment(ref.nativeElement, this.componentList[i].svg));
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    private appendFragment(host: SVGElement, fragment: string) {
        while (host.firstChild) {
            host.removeChild(host.firstChild);
        }
        if (!fragment || !fragment.trim()) return;
        const wrapped = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">${fragment}</svg>`;
        const doc = new DOMParser().parseFromString(wrapped, 'image/svg+xml');
        const nodes = Array.from(doc.documentElement.childNodes);
        nodes.forEach(node => {
            const imported = host.ownerDocument.importNode(node, true);
            host.appendChild(imported);
        });
        const elementsWithClip = host.querySelectorAll('[clip-path]');
        elementsWithClip.forEach(el => {
            const clipAttr = el.getAttribute('clip-path') || '';
            const match = clipAttr.match(/#([^)]+)/);
            if (match) {
                const clipId = match[1].replace(/['"]/g, '');
                if (!host.querySelector(`#${CSS.escape(clipId)}`)) {
                    el.removeAttribute('clip-path');
                }
            }
        });
        try {
            const graphicHost = host as unknown as SVGGraphicsElement;
            const bbox = graphicHost.getBBox();
            if (bbox && (bbox.width > 0 || bbox.height > 0)) {
                const padding = Math.max(16, Math.max(bbox.width, bbox.height) * 0.15);
                host.setAttribute(
                    'viewBox',
                    `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
                );
                return;
            }
        } catch (e) {
            console.warn('Could not calculate preview viewBox:', e);
        }
        host.setAttribute('viewBox', '0 0 100 100');
    }

    openComponentOptions(event: MouseEvent, component: LibraryComponent) {
        if (this.isSettingCondition) return;
        event.preventDefault();
        event.stopPropagation();
        if (!this.componentsContainer) return;
        const componentPosition = this.componentsContainer.nativeElement.getBoundingClientRect();

        const clientX = event.clientX;
        const clientY = event.clientY;

        this.optionsTop = clientY + 'px';
        this.optionsLeft = clientX + 'px';
        this.selectedComponent = component;

        if (componentPosition.height < clientY + this.MAX_HEIGHT) {
            this.optionsTop = clientY - this.MAX_HEIGHT + 'px';
        }

        this.showComponentOption = true;

        this.cdr.detectChanges();
    }

    async deleteComponent() {
        this.onDeleteComponent.emit(this.selectedComponent);
    }

    addToCanvas(event: MouseEvent, component: LibraryComponent) {
        if (this.isSettingCondition) {
            this.selectedComponent = component;
            this.onSelectComponent.emit(this.selectedComponent);
            return;
        }
        if (event) {
            if (this.showComponentOption) {
                this.showComponentOption = false;
            }
            const container = event.currentTarget as HTMLElement;
            const svg = container.querySelector('svg') as SVGSVGElement | null;
            if (!svg) return;
            const all = svg.children[0];
            if (!all) return;
            this.componentSvg = all.cloneNode(true) as HTMLElement;
        }
        const newElementId = this.canvas.getNextId();
        this.componentSvg.setAttribute(ElementAttribute.id, newElementId);
        Array.from(this.componentSvg.children).forEach((element: Element) => {
            const newElementId = this.canvas.getNextId();
            element.setAttribute(ElementAttribute.id, newElementId);
        });

        const svgRoot = this.canvasService.getSvgRoot() as unknown as HTMLElement;
        const layer = svgRoot.querySelector('.layer');
        layer.appendChild(this.componentSvg);
    }

    async duplicateComponent() {
        const selectedComponent = this.selectedComponent;
        const existingNames = this.componentList.map(component => component.name) || [];
        try {
            const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                data: {
                    title: 'mimic.duplicate.duplicate-component.title',
                    initialName: this.selectedComponent.name,
                    existingNames,
                    type: DuplicateItemType.COMPONENT,
                    submitButtonLabel: ButtonLabel.DUPLICATE,
                    autoIncrementName: true,
                },
            });
            const result = await firstValueFrom(dialogRef.afterClosed());
            if (!result.name) return;

            const newComponent = {
                ...selectedComponent,
                name: result.name,
            };
            await this.libraryComponentService.duplicateComponent(newComponent);
        } catch (error) {
            console.log(error);
        }
    }
}
